/**
 * Turns whatever was thrown into something an error screen can show.
 *
 * Kept free of React so it can be unit-tested and so the last-resort boundary
 * in main.tsx — which sits outside the router — can use it too.
 *
 * A route can surface three different shapes: a real `Error` from a component
 * that crashed while rendering, a router "response" (a 404 for an address that
 * matches nothing), or any other value, because JavaScript lets you `throw` a
 * string. The screen needs to tell a missing page from a crash, and needs one
 * safe line for the operator and the full text for whoever fixes it.
 */

import { isRouteErrorResponse } from 'react-router'

export type ErrorKind = 'notFound' | 'crash'

export interface DescribedError {
  kind: ErrorKind
  /** One line. Shown in the technical details, never as the headline. */
  summary: string
  /** Everything a developer needs: message and stack, or status and body. */
  detail: string
}

export function describeError(error: unknown): DescribedError {
  if (isRouteErrorResponse(error)) {
    const summary = `${error.status} ${error.statusText}`.trim()
    const body = typeof error.data === 'string' ? error.data : safeJson(error.data)
    return {
      kind: error.status === 404 ? 'notFound' : 'crash',
      summary,
      detail: [summary, body].filter(Boolean).join('\n'),
    }
  }

  if (error instanceof Error) {
    const summary = error.message || error.name
    const stack = error.stack ?? ''
    // V8 stacks begin with "Name: message"; Safari's and Firefox's are frames
    // only. Without the message a copied report from those is just line numbers.
    const detail =
      stack && stack.includes(summary) ? stack : [`${error.name}: ${error.message}`, stack].filter(Boolean).join('\n')
    return { kind: 'crash', summary, detail }
  }

  const text = typeof error === 'string' ? error : safeJson(error)
  return { kind: 'crash', summary: text || 'Unknown error', detail: text || 'Unknown error' }
}

/**
 * The text behind "Salin detail".
 *
 * An operator on an event floor is not going to open devtools. What they can
 * do is press one button and paste the result into a chat — which is exactly
 * how the #185 crash reached us — so the report carries the context a stack
 * trace alone does not: when, on which page, and in which browser.
 */
export function errorReport(
  described: DescribedError,
  context: { at: Date; href: string; userAgent: string },
): string {
  return [
    'Citaria console — error report',
    `Time: ${context.at.toISOString()}`,
    `Page: ${context.href}`,
    `Browser: ${context.userAgent}`,
    '',
    described.detail,
  ].join('\n')
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    // Circular, or holds a BigInt. The error screen must not throw while
    // describing an error.
    return String(value)
  }
}
